import { html, TemplateResult, nothing } from 'lit';

import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './se-sidebar-layout.css.lit';
import { SlotController } from '../../controllers/slot-controller';

/**
 * The Sidebar Layout component.
 * @element 'sidebar-layout'
 */
@customElement('sidebar-layout')
export class SidebarLayout extends OutlineElement {
  slots = new SlotController(this, false);
  static styles = [componentStyles];

  private isMobile: boolean;

  /**
   * Changes spacing in the sidebar for when content is a snug fit.
   */
  @property({ type: Boolean, reflect: true, attribute: 'snug-fit' })
  snugFit = false;

  @state() hasDisclaimerSlot: boolean;

  mobileMode() {
    const maxMobileWidth = 768 - 1;
    // Set initial value to isMobile
    this.isMobile = window.innerWidth < maxMobileWidth;
    const mediaQuery = `(max-width: ${maxMobileWidth}px)`;
    const mediaQueryList = window.matchMedia(mediaQuery);

    // update isMobile value only when screen width is crossing maxMobileWidth (767)
    mediaQueryList.addEventListener('change', event => {
      this.isMobile = event.matches;
      this.requestUpdate();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.mobileMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  firstUpdated() {
    this.hasDisclaimerSlot = this.slots.test('disclaimer');
  }

  render(): TemplateResult {
    return html`
      <div
        class="wrapper
          ${this.isMobile ? 'is-mobile' : 'is-desktop'}
          ${this.snugFit ? 'snug-fit' : null}"
      >
        ${this.isMobile
          ? html`
              <slot name="primary-sidebar"></slot>
              <slot name="main-content"></slot>
              ${this.hasDisclaimerSlot
                ? html`<slot name="disclaimer"></slot>`
                : nothing}
              <slot name="secondary-sidebar"></slot>
            `
          : html`
              <div class="sidebar">
                <slot name="primary-sidebar"></slot>
                <slot name="secondary-sidebar"></slot>
              </div>
              <div class="main-content">
                <slot name="main-content"></slot>
                ${this.hasDisclaimerSlot
                  ? html`<slot name="disclaimer"></slot>`
                  : nothing}
              </div>
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sidebar-layout': SidebarLayout;
  }
}
